// src/pages/admin/AdminAddListing.js
// Formulaire d'ajout - AUCUN champ obligatoire, Type en liste + ajout libre - NE PAS MODIFIER

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import { CATEGORIES } from '../../data/categories';
import { LISTING_STATUS } from '../../constants/listingStatus';
import apiClient from '../../api/client';

const NEW_TYPE_VALUE = '__new__';

const AdminAddListing = () => {
  const navigate = useNavigate();
  const [category, setCategory] = useState('locations');
  const [form, setForm] = useState({
    title: '', type: '', quartier: '', city: 'Douala', price: '',
    bedrooms: '', bathrooms: '', area: '', capacity: '', floor: '',
    legalStatus: '', viabilise: false, cloture: false, proLabel: '', coveredAreas: '',
    description: '', descriptionEn: '', lat: '', lng: '',
    ownerName: '', ownerPhone: '', amenities: '', status: LISTING_STATUS.DRAFT,
    isSponsored: false,
  });
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const [quartierSuggestions, setQuartierSuggestions] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [typesByCategory, setTypesByCategory] = useState({});
  const [selectedTypeOption, setSelectedTypeOption] = useState('');
  const [customType, setCustomType] = useState('');

  const [placeQuery, setPlaceQuery] = useState('');
  const [placeResults, setPlaceResults] = useState([]);
  const [searchingPlace, setSearchingPlace] = useState(false);

  useEffect(() => {
    const loadSuggestions = async () => {
      try {
        const [quartiersRes, typesRes] = await Promise.all([
          apiClient.get('/endpoints/quartiers.php?action=list'),
          apiClient.get('/endpoints/types.php?action=list'),
        ]);
        const quartiers = quartiersRes.quartiers || [];
        setQuartierSuggestions(quartiers.map((q) => q.name));
        setCitySuggestions([...new Set(quartiers.map((q) => q.city))]);
        setTypesByCategory(typesRes.types || {});
      } catch (err) {
        // Pas grave, les champs restent utilisables quand même
      }
    };
    loadSuggestions();
  }, []);

  const currentTypeOptions = typesByCategory[category] || [];

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTypeSelect = (value) => {
    setSelectedTypeOption(value);
    if (value === NEW_TYPE_VALUE) {
      handleChange('type', '');
    } else {
      handleChange('type', value);
      setCustomType('');
    }
  };

  const handleCustomTypeChange = (value) => {
    setCustomType(value);
    handleChange('type', value);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setImages((prev) => [...prev, ...newImages].slice(0, 12));
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const searchPlace = useCallback((query) => {
    setPlaceQuery(query);
    if (query.trim().length < 3) {
      setPlaceResults([]);
      return;
    }
    setSearchingPlace(true);
    apiClient
      .get(`/endpoints/geocode.php?action=search&q=${encodeURIComponent(query)}`)
      .then((res) => setPlaceResults(res.results || []))
      .catch(() => setPlaceResults([]))
      .finally(() => setSearchingPlace(false));
  }, []);

  const handleSelectPlace = (place) => {
    setForm((prev) => ({
      ...prev,
      quartier: place.name.split(',')[0].trim(),
      city: place.city || prev.city,
      lat: place.lat ?? prev.lat,
      lng: place.lng ?? prev.lng,
    }));
    setPlaceQuery('');
    setPlaceResults([]);
  };

  // Aucun champ n'est obligatoire : on envoie tel quel, le serveur complète les valeurs manquantes
  const handleSubmit = async (publishNow = false) => {
    setSuccessMsg('');
    setSaving(true);
    try {
      const listingData = {
        ...form,
        category,
        status: publishNow ? LISTING_STATUS.PUBLISHED : form.status,
        amenities: form.amenities ? form.amenities.split(',').map((a) => a.trim()) : [],
      };

      const created = await apiClient.post('/endpoints/listings.php?action=create', listingData);

      for (const img of images) {
        const formData = new FormData();
        formData.append('listingId', created.id);
        formData.append('image', img.file);
        try {
          await apiClient.uploadFile('/endpoints/upload.php?action=image', formData);
        } catch (uploadErr) {
          console.error('Erreur upload image:', uploadErr);
        }
      }

      navigate(ROUTES.ADMIN_LISTINGS);
    } catch (err) {
      // Ne bloque jamais silencieusement : on affiche l'erreur réelle du serveur s'il y en a une,
      // mais on ne l'invente jamais côté frontend
      setSuccessMsg('');
      alert(err.message || "Une erreur serveur est survenue, réessaie.");
    } finally {
      setSaving(false);
    }
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '900px', margin: '0 auto', backgroundColor: '#0A0E1A', paddingBottom: '80px' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', position: 'sticky', top: 0, backgroundColor: '#0A0E1A', zIndex: 50 },
    backButton: { background: 'none', border: 'none', color: '#FFFFFF', fontSize: '20px', cursor: 'pointer' },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF' },
    form: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
    categoryTabs: { display: 'flex', gap: '8px', overflowX: 'auto', scrollbarWidth: 'none' },
    catTab: { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', borderRadius: '10px', backgroundColor: '#162033', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#A0AEC0', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    catTabActive: { backgroundColor: 'rgba(255, 215, 0, 0.15)', border: '1px solid rgba(255, 215, 0, 0.3)', color: '#FFD700' },
    field: { display: 'flex', flexDirection: 'column', gap: '6px' },
    row: { display: 'flex', gap: '12px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#A0AEC0' },
    optional: { color: '#4A5568', fontWeight: '400', fontSize: '11px' },
    hint: { fontSize: '11px', color: '#4A5568' },
    input: { padding: '12px 14px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '15px', outline: 'none', width: '100%' },
    select: { padding: '12px 14px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '15px', outline: 'none', width: '100%' },
    textarea: { padding: '12px 14px', backgroundColor: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#FFFFFF', fontSize: '15px', outline: 'none', minHeight: '90px', resize: 'vertical', fontFamily: 'inherit' },
    checkbox: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#FFFFFF', cursor: 'pointer' },
    sponsoredBox: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', backgroundColor: 'rgba(255, 215, 0, 0.08)', border: '1px solid rgba(255, 215, 0, 0.3)', borderRadius: '10px', cursor: 'pointer' },
    sponsoredIcon: { fontSize: '20px' },
    sponsoredTextGroup: { display: 'flex', flexDirection: 'column', gap: '2px' },
    sponsoredLabel: { fontSize: '14px', fontWeight: '700', color: '#FFD700' },
    sponsoredDesc: { fontSize: '12px', color: '#A0AEC0' },
    imageUpload: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
    uploadBtn: { width: '80px', height: '80px', borderRadius: '8px', border: '2px dashed rgba(255, 215, 0, 0.3)', backgroundColor: '#111827', color: '#FFD700', fontSize: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    imagePreview: { position: 'relative', width: '80px', height: '80px' },
    previewImg: { width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover' },
    removeImgBtn: { position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#F56565', color: '#FFFFFF', border: 'none', fontSize: '12px', cursor: 'pointer' },
    sectionLabel: { fontSize: '14px', fontWeight: '700', color: '#FFD700', marginTop: '8px' },
    placeSearchBox: { padding: '14px', backgroundColor: 'rgba(66, 153, 225, 0.08)', border: '1px solid rgba(66, 153, 225, 0.3)', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '8px' },
    placeResults: { display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' },
    placeResultItem: { padding: '10px 12px', backgroundColor: '#111827', borderRadius: '8px', fontSize: '13px', color: '#FFFFFF', cursor: 'pointer', border: '1px solid rgba(255, 255, 255, 0.05)' },
    bottomBar: { position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '900px', display: 'flex', gap: '10px', padding: '12px 20px', backgroundColor: '#0A0E1A', borderTop: '1px solid rgba(255, 255, 255, 0.1)' },
    draftBtn: { flex: 1, padding: '14px', backgroundColor: '#162033', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '10px', color: '#FFFFFF', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    publishBtn: { flex: 1, padding: '14px', backgroundColor: '#FFD700', border: 'none', borderRadius: '10px', color: '#0A0E1A', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Ajouter une annonce | Fynho Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_LISTINGS)}>←</button>
        <div style={styles.title}>Nouvelle annonce</div>
      </div>

      <div style={styles.form}>
        <div style={styles.field}>
          <label style={styles.label}>Catégorie</label>
          <div style={styles.categoryTabs} className="hide-scrollbar">
            <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
            {CATEGORIES.map((cat) => (
              <div
                key={cat.id}
                style={{ ...styles.catTab, ...(category === cat.id ? styles.catTabActive : {}) }}
                onClick={() => { setCategory(cat.id); setSelectedTypeOption(''); setCustomType(''); handleChange('type', ''); }}
              >
                <span>{cat.icon}</span>
                {cat.id === 'immobilier' ? 'Parcelles' : cat.id}
              </div>
            ))}
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Titre <span style={styles.optional}>(facultatif)</span></label>
          <input type="text" style={styles.input} value={form.title} onChange={(e) => handleChange('title', e.target.value)} placeholder="Ex: Appartement moderne meublé 2 chambres" />
        </div>

        <div style={styles.placeSearchBox}>
          <label style={styles.label}>🔍 Rechercher un lieu (optionnel, remplit automatiquement)</label>
          <input
            type="text"
            style={styles.input}
            value={placeQuery}
            onChange={(e) => searchPlace(e.target.value)}
            placeholder="Ex: Ndogbong, Douala..."
          />
          {searchingPlace && <span style={styles.hint}>Recherche...</span>}
          {placeResults.length > 0 && (
            <div style={styles.placeResults}>
              {placeResults.map((place, index) => (
                <div key={index} style={styles.placeResultItem} onClick={() => handleSelectPlace(place)}>
                  📍 {place.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Type : vraie liste déroulante + option pour ajouter un nouveau type libre */}
        <div style={styles.field}>
          <label style={styles.label}>Type <span style={styles.optional}>(facultatif)</span></label>
          <select style={styles.select} value={selectedTypeOption} onChange={(e) => handleTypeSelect(e.target.value)}>
            <option value="">Choisir un type existant</option>
            {currentTypeOptions.map((t) => (
              <option key={t.rowId || t.id} value={t.name}>{t.name}</option>
            ))}
            <option value={NEW_TYPE_VALUE}>➕ Ajouter un nouveau type...</option>
          </select>
          {selectedTypeOption === NEW_TYPE_VALUE && (
            <input
              type="text"
              style={styles.input}
              value={customType}
              onChange={(e) => handleCustomTypeChange(e.target.value)}
              placeholder="Nom du nouveau type (ex: Bureau, Duplex...)"
              autoFocus
            />
          )}
        </div>

        <div style={styles.row}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Ville <span style={styles.optional}>(facultatif)</span></label>
            <input
              type="text"
              style={styles.input}
              list="city-suggestions"
              value={form.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Ex: Douala"
            />
            <datalist id="city-suggestions">
              {citySuggestions.map((c, i) => (<option key={i} value={c} />))}
            </datalist>
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Quartier <span style={styles.optional}>(facultatif)</span></label>
            <input
              type="text"
              style={styles.input}
              list="quartier-suggestions"
              value={form.quartier}
              onChange={(e) => handleChange('quartier', e.target.value)}
              placeholder="Ex: Bonapriso ou un nouveau nom"
            />
            <datalist id="quartier-suggestions">
              {quartierSuggestions.map((q, i) => (<option key={i} value={q} />))}
            </datalist>
            <span style={styles.hint}>Pas trouvé dans la liste ? Tape simplement le nom, il sera accepté.</span>
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>
            Prix (FCFA) <span style={styles.optional}>(facultatif)</span>
            {category === 'sejours' && ' / nuit'}
            {category === 'locations' && ' / mois'}
            {category === 'immobilier' && ' / m²'}
          </label>
          <input type="number" style={styles.input} value={form.price} onChange={(e) => handleChange('price', e.target.value)} placeholder="Ex: 250000" />
        </div>

        {(category === 'sejours' || category === 'locations') && (
          <>
            <div style={styles.sectionLabel}>Détails du logement</div>
            <div style={styles.row}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Chambres</label>
                <input type="number" style={styles.input} value={form.bedrooms} onChange={(e) => handleChange('bedrooms', e.target.value)} placeholder="2" />
              </div>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Salles de bain</label>
                <input type="number" style={styles.input} value={form.bathrooms} onChange={(e) => handleChange('bathrooms', e.target.value)} placeholder="1" />
              </div>
            </div>
            <div style={styles.row}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Superficie (m²)</label>
                <input type="number" style={styles.input} value={form.area} onChange={(e) => handleChange('area', e.target.value)} placeholder="85" />
              </div>
              {category === 'sejours' && (
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label}>Capacité (pers.)</label>
                  <input type="number" style={styles.input} value={form.capacity} onChange={(e) => handleChange('capacity', e.target.value)} placeholder="4" />
                </div>
              )}
              {category === 'locations' && (
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label}>Étage</label>
                  <input type="text" style={styles.input} value={form.floor} onChange={(e) => handleChange('floor', e.target.value)} placeholder="RDC, 1er..." />
                </div>
              )}
            </div>
          </>
        )}

        {category === 'immobilier' && (
          <>
            <div style={styles.sectionLabel}>Détails de la parcelle</div>
            <div style={styles.field}>
              <label style={styles.label}>Superficie (m²)</label>
              <input type="number" style={styles.input} value={form.area} onChange={(e) => handleChange('area', e.target.value)} placeholder="500" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Statut juridique</label>
              <input type="text" style={styles.input} value={form.legalStatus} onChange={(e) => handleChange('legalStatus', e.target.value)} placeholder="Titre foncier, ..." />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Label professionnel</label>
              <input type="text" style={styles.input} value={form.proLabel} onChange={(e) => handleChange('proLabel', e.target.value)} placeholder="Parcelle viabilisée" />
            </div>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={form.viabilise} onChange={(e) => handleChange('viabilise', e.target.checked)} />
              Viabilisé
            </label>
            <label style={styles.checkbox}>
              <input type="checkbox" checked={form.cloture} onChange={(e) => handleChange('cloture', e.target.checked)} />
              Clôturé
            </label>
          </>
        )}

        {category === 'services' && (
          <div style={styles.field}>
            <label style={styles.label}>Quartiers couverts</label>
            <input type="text" style={styles.input} value={form.coveredAreas} onChange={(e) => handleChange('coveredAreas', e.target.value)} placeholder="Bonapriso, Akwa, Bonanjo..." />
          </div>
        )}

        {category !== 'immobilier' && category !== 'services' && (
          <div style={styles.field}>
            <label style={styles.label}>Équipements (séparés par virgule)</label>
            <input type="text" style={styles.input} value={form.amenities} onChange={(e) => handleChange('amenities', e.target.value)} placeholder="Wifi, Climatisation, Parking, Sécurité" />
          </div>
        )}

        <div style={styles.sectionLabel}>Mise en avant</div>
        <label style={styles.sponsoredBox}>
          <input type="checkbox" checked={form.isSponsored} onChange={(e) => handleChange('isSponsored', e.target.checked)} />
          <span style={styles.sponsoredIcon}>⭐</span>
          <div style={styles.sponsoredTextGroup}>
            <span style={styles.sponsoredLabel}>Annonce sponsorisée / premium</span>
            <span style={styles.sponsoredDesc}>Apparaîtra en priorité dans une section spéciale sur l'accueil</span>
          </div>
        </label>

        <div style={styles.sectionLabel}>Photos</div>
        <div style={styles.imageUpload}>
          {images.map((img, index) => (
            <div key={index} style={styles.imagePreview}>
              <img src={img.preview} alt={`upload ${index}`} style={styles.previewImg} />
              <button style={styles.removeImgBtn} onClick={() => removeImage(index)}>✕</button>
            </div>
          ))}
          {images.length < 12 && (
            <label style={styles.uploadBtn}>
              +
              <input type="file" accept="image/*" multiple onChange={handleImageUpload} style={{ display: 'none' }} />
            </label>
          )}
        </div>

        <div style={styles.sectionLabel}>Descriptions</div>
        <div style={styles.field}>
          <label style={styles.label}>Description (Français)</label>
          <textarea style={styles.textarea} value={form.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Décrivez le bien..." />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Description (English)</label>
          <textarea style={styles.textarea} value={form.descriptionEn} onChange={(e) => handleChange('descriptionEn', e.target.value)} placeholder="Describe the property..." />
        </div>

        <div style={styles.sectionLabel}>Localisation GPS (auto-remplie si tu utilises la recherche de lieu)</div>
        <div style={styles.row}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Latitude</label>
            <input type="text" style={styles.input} value={form.lat} onChange={(e) => handleChange('lat', e.target.value)} placeholder="4.0511" />
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Longitude</label>
            <input type="text" style={styles.input} value={form.lng} onChange={(e) => handleChange('lng', e.target.value)} placeholder="9.7679" />
          </div>
        </div>

        <div style={styles.sectionLabel}>Contact propriétaire</div>
        <div style={styles.field}>
          <label style={styles.label}>Nom du propriétaire</label>
          <input type="text" style={styles.input} value={form.ownerName} onChange={(e) => handleChange('ownerName', e.target.value)} placeholder="Ex: M. Kamga" />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>WhatsApp / Téléphone <span style={styles.optional}>(facultatif)</span></label>
          <input
            type="tel"
            style={styles.input}
            value={form.ownerPhone}
            onChange={(e) => handleChange('ownerPhone', e.target.value)}
            onBlur={(e) => handleChange('ownerPhone', e.target.value)}
            placeholder="+237 6XX XXX XXX"
            autoComplete="tel"
          />
        </div>
      </div>

      <div style={styles.bottomBar}>
        <button style={{ ...styles.draftBtn, opacity: saving ? 0.6 : 1 }} onClick={() => handleSubmit(false)} disabled={saving}>
          Enregistrer brouillon
        </button>
        <button style={{ ...styles.publishBtn, opacity: saving ? 0.6 : 1 }} onClick={() => handleSubmit(true)} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Publier'}
        </button>
      </div>
    </div>
  );
};

export default AdminAddListing;